import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';
import auth from 'services/auth';
import actions from 'partners/actions';
import { parseQuery } from 'utils';
import { getPartnerIdFromPath } from 'utils/container';
import { applyTheme, getStoredTheme } from 'utils/theme';

const { BASE_URI } = process.env;

const mapStateToProps = (state) => ({
  loading: state.loading,
  session: state.session,
  partner: state.partner,
  user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
  fetchPartner: () => {
    return dispatch(actions.partner.fetch());
  },

  fetchSession() {
    return dispatch(actions.session.fetch());
  },

  fetchUser: (query) => {
    return dispatch(actions.user.fetch(query));
  },

  userLogout: () => {
    return dispatch(actions.user.performLogout());
  },

  switchPartner: (partnerId) => {
    return dispatch(actions.user.switchPartner(partnerId));
  },

  flashError: (message) => {
    dispatch(actions.flash.error(message));
  },

  clearFlash: (event) => {
    if (event) event.preventDefault();
    dispatch(actions.flash.clear());
  },

  cancelAutoFlash: (event) => {
    if (event) event.preventDefault();
    actions.flash.cancelAuto();
  },
});

function insertCss(...styles) {
  const removeCss = styles.map((x) => x._insertCss());

  return () => {
    removeCss.forEach((f) => f());
  };
}

export class App extends React.Component {
  static propTypes = {
    partner: pt.object,
    loading: pt.bool,
    location: pt.object,
    session: pt.object,
    user: pt.object,
    flash: pt.object,
    router: pt.object,
    children: pt.node,
    clearFlash: pt.func,
    cancelAutoFlash: pt.func,
    fetchPartner: pt.func,
    fetchUser: pt.func,
    fetchSession: pt.func,
    userLogout: pt.func,
    switchPartner: pt.func,
    flashError: pt.func,
  };

  static childContextTypes = {
    session: pt.object,
    partner: pt.object,
    flash: pt.object,
    user: pt.object,
    isOwner: pt.bool,
    location: pt.object,
    clearFlash: pt.func,
    cancelAutoFlash: pt.func,
    router: pt.object,
    setTitle: pt.func,
    getLastUrl: pt.func,
    setLastUrl: pt.func,
    onClickLogout: pt.func,
    onClickSwitchPartner: pt.func,
    insertCss: pt.func,
  };

  state = {
    loaded: false,
  };

  bodyLoadingEl = null;

  async componentDidMount() {
    const { fetchPartner, fetchUser, fetchSession, router } = this.props;

    applyTheme(getStoredTheme());

    this.clearSession();
    this.enhanceRouter(this.props);
    this.enhanceLocationQuery(this.props);
    this.bodyLoadingEl = document.getElementById('loading');

    const urlPartnerId = getPartnerIdFromPath();
    // One-release migration: adopt a pre-scoping bare session cookie
    const legacySessionId = auth.getLegacySessionCandidate(urlPartnerId);

    const session = await fetchSession();

    if (legacySessionId) {
      // Consumed regardless of validity so a stale cookie can't loop the boot
      auth.removeLegacySessionCookie();
    }

    // Session belongs to a different partner than the one in the url.
    // Reload so no session state carries over from the other partner
    if (
      urlPartnerId &&
      session?.partner_id &&
      session.partner_id !== urlPartnerId
    ) {
      auth.removeSessionCookie(urlPartnerId);
      window.location.replace(`/${urlPartnerId}/login`);

      return;
    }

    const isLoggedIn = Boolean(session?.user_id);

    if (isLoggedIn) {
      const [partner] = await Promise.all([fetchPartner(), fetchUser()]);

      if (!partner) {
        router.replace('/login');
      }
    }

    this.setState({ loaded: true });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.partner !== this.props.partner) {
      this.setTitle();
    }

    if (this.bodyLoadingEl && prevProps.loading && !this.props.loading) {
      this.bodyLoadingEl.outerHTML = '';
      this.bodyLoadingEl = null;
    }

    // Theme is a partner-dashboard-local preference, not synced from user.theme.
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.enhanceLocationQuery(nextProps);
  }

  clearSession() {
    const { location } = this.props;
    // Whole segments: a partner id may legitimately start with one of these
    const segments = location.pathname.split('/').filter(Boolean);
    const shouldClear = ['signup', 'join', 'verify-email'].some((segment) =>
      segments.includes(segment),
    );

    if (shouldClear) {
      auth.removeSessionCookie();
    }
  }

  getChildContext() {
    return {
      session: this.props.session,
      partner: this.props.partner,
      flash: this.props.flash,
      user: this.props.user,
      isOwner: this.isOwner(),
      location: this.props.location,
      clearFlash: this.props.clearFlash,
      cancelAutoFlash: this.props.cancelAutoFlash,
      router: this.props.router,
      setTitle: this.setTitle,
      getLastUrl: this.getLastUrl,
      setLastUrl: this.setLastUrl,
      onClickLogout: this.onClickLogout,
      onClickSwitchPartner: this.onClickSwitchPartner,
      insertCss: insertCss,
    };
  }

  getNextUri(uri) {
    // Routes without a partner prefix (/login, /signup) fall back to the
    // partner the session was established for
    const partnerId = getPartnerIdFromPath() || auth.getSessionPartnerId();
    let nextUri = typeof uri === 'string' ? uri : uri.pathname;

    nextUri = nextUri.replace(BASE_URI, '');

    if (!partnerId) {
      return nextUri;
    }

    // The first path segment must be the partner id, not merely contain it
    const [, firstSegment = ''] = nextUri.split(/[/?#]/);

    return firstSegment === partnerId
      ? nextUri
      : `/${partnerId}${nextUri}`.replace(/\/$/, '');
  }

  enhanceRouter(props) {
    if (!props.router.enhancedWithBaseUri) {
      props.router.originalPush = props.router.push;
      props.router.originalReplace = props.router.replace;
    }

    props.router.push = (uri, ...args) =>
      props.router.originalPush(this.getNextUri(uri), ...args);

    props.router.replace = (uri, ...args) =>
      props.router.originalReplace(this.getNextUri(uri), ...args);

    props.router.enhancedWithBaseUri = true;
  }

  enhanceLocationQuery(props) {
    if (props.location.queryParsed !== props.location.query) {
      props.location.query = parseQuery(props.location.search);
      props.location.queryParsed = props.location.query;
    }
  }

  setTitle = (section = '') => {
    const { partner } = this.props;
    const partnerName = getPartnerIdFromPath() ? partner.name : null;

    document.title = `${section ? `${section} / ` : ''}${
      partnerName || 'Swell'
    }`;
  };

  getLastUrl = () => {
    return App.lastUrl;
  };

  setLastUrl = (url) => {
    if (url) {
      App.lastUrl = url;
    }
  };

  isOwner() {
    const { user, partner } = this.props;

    return partner.owner_id === user.id;
  }

  onClickLogout = (event) => {
    event.preventDefault();
    this.props.router.push('/login');
    this.props.userLogout();
  };

  onClickSwitchPartner = (event) => {
    event.preventDefault();
    const { partner, switchPartner, flashError } = this.props;
    const { id } = event.currentTarget.dataset;

    if (!id || id === partner.id) {
      return;
    }

    return switchPartner(id)
      .then((result) => {
        if (!result) {
          flashError('Access denied');
        }
      })
      .catch((err) => {
        flashError(err?.message || 'Access denied');
      });
  };

  render() {
    const { children } = this.props;
    const { loaded } = this.state;

    if (!loaded) {
      return null;
    }

    return children;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
